import { yupResolver } from "@hookform/resolvers/yup";
import CloseIcon from "@mui/icons-material/Close";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SaveIcon from "@mui/icons-material/Save";
import { LoadingButton } from "@mui/lab";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Stack,
    Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import ExportSimulation from "components/ExportSimulation";
import { FormProvider } from "components/hook-form";
// import { useUploadFile } from "hooks/useFileUploader";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { readClipboard } from "utils/readClipboard";
import * as Yup from "yup";

import { useGetSimulationBulkMutation, useSimulateBulkMutation } from "../helpers/simulations.queries";

type PromotionFormDialogProps = {
    open: boolean;
    onClose: () => void;
};

export const CreateSimulationsDialog = ({ open, onClose }: PromotionFormDialogProps) => {
    // const { mutateAsync: uploadFile } = useUploadFile();
    const { mutateAsync: simulateBulk } = useSimulateBulkMutation();
    const { mutateAsync: getSimulationStatusBulk } = useGetSimulationBulkMutation();
    const [isImporting, setIsImporting] = useState(false);

    const Schema = Yup.object().shape({
        items: Yup.array().of(
            Yup.object().shape({
                id: Yup.number().required("ID is required"),
                patientId: Yup.string().required("Patient ID is required"),
                original: Yup.string().required("original is required"),
            })
        ),
    });

    const defaultValues: {
        items: {
            id: number;
            patientId: string;
            original: string;
        }[];
    } = {
        items: [],
    };

    const methods = useForm({
        resolver: yupResolver(Schema),
        defaultValues,
    });

    const {
        handleSubmit,
        formState: { isSubmitting, errors },
        control,
        reset,
        setValue,
        getValues,
        watch,
    } = methods;

    useEffect(() => {
        if (errors.items) {
            errors.items.forEach((error, index) => {
                if (error.patientId) {
                    control.setError(`items.${index}.patientId`, {
                        type: "manual",
                    });
                }
                if (error.original) {
                    control.setError(`items.${index}.original`, {
                        type: "manual",
                    });
                }
            });
        }
    }, [errors, control]);

    const onSubmit = async (values) => {
        try {
            const res = await simulateBulk(values.items);

            const ids = res.map((item) => item._id);

            const result = await getSimulationStatusBulk({
                ids,
                callback: (data: Record<string, { status: string }>) => {
                    Object.entries(data).forEach(([key, value]) => {
                        console.log(key, value);
                        toast.success(`Simulation ${key} is ${value.status}`);
                    });
                },
            });

            console.log(result);

            reset(defaultValues);

            onClose();
        } catch (error) {
            console.error(error);
            toast.error("Failed to create simulations");
        }
    };

    // const [uploadingFile, setUploadingFile] = useState<{ [lang: string]: boolean }>();

    // const handleFileUpload = async (lang: ILanguage, file: File) => {
    //     const reader = new FileReader();

    //     setUploadingFile({ ...uploadingFile, [lang.code]: true });
    //     reader.onload = () => {
    //         if (typeof reader.result === "string") {
    //             const date = new Date();
    //             const year = date.getFullYear();
    //             const month = date.getMonth() + 1;
    //             const day = date.getDate();

    //             const hash = Math.random().toString(36).substring(7);

    //             const base64String = reader.result as string;
    //             uploadFile({
    //                 bucketName: "elitai",
    //                 fileKey: `test/promotions/${lang.code}/${year}-${month}-${day}/${hash}_${file.name}`,
    //                 contentEncoding: "base64",
    //                 contentType: file.type,
    //                 content: base64String,
    //             })
    //                 .then((response) => {
    //                     const url = response.data.Location;
    //                 })
    //                 .finally(() => {
    //                     setUploadingFile((files) => {
    //                         delete files[lang.code];
    //                         return files;
    //                     });
    //                 });
    //         } else {
    //             throw new Error("Invalid file type");
    //         }
    //     };
    //     reader.readAsDataURL(file);
    // };

    // Data grid props
    const columns = [
        {
            field: "patientId",
            headerName: "Patient ID",
            flex: 1,
            editable: true,
        },
        {
            field: "original",
            headerName: "Original Image URL",
            flex: 1,
            editable: true,
        },
        {
            field: "preview",
            headerName: "Preview",
            width: 120,
            renderCell: (params) => {
                if (params.row.original.startsWith("http")) {
                    return (
                        <img
                            src={params.row.original}
                            alt={params.row.original}
                            style={{ width: 100, height: 100, objectFit: "scale-down" }}
                        />
                    );
                }
                return (
                    <Typography variant="caption" color="text.secondary">
                        No preview
                    </Typography>
                );
            },
        },
        {
            field: "actions",
            headerName: "Actions",
            width: 120,
            renderCell: (params) => (
                <Stack direction="row" spacing={1} alignContent={"end"}>
                    <Button
                        color="error"
                        variant="outlined"
                        size="small"
                        onClick={() => {
                            const items = getValues("items");
                            setValue(
                                "items",
                                items.filter((item) => item.id !== params.row.id)
                            );
                        }}
                    >
                        Delete
                    </Button>
                </Stack>
            ),
        },
    ];

    const handleImportFromClipboard = async () => {
        try {
            setIsImporting(true);
            const clipboard = await readClipboard();
            const rows = clipboard.split("\n").map((row) => row.split("\t"));

            const filteredCols = rows.filter((row) => row[0].length > 0 && row[1].length > 0);

            const items = filteredCols.map((row, index) => ({
                id: index + 1,
                patientId: row[0],
                original: row[1],
            }));

            setValue("items", items);
            toast.success(`Successfully imported ${items.length} items`);
        } catch (error) {
            console.error(error);
            toast.error("Failed to import from clipboard");
        } finally {
            setIsImporting(false);
        }
    };

    const itemsCount = watch("items")?.length || 0;

    return (
        <Dialog
            fullWidth
            maxWidth="md"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    overflow: "hidden",
                },
            }}
        >
            <DialogTitle sx={{ bgcolor: "primary.main", color: "primary.contrastText", py: 2 }}>
                <Typography variant="h6">Create Simulations</Typography>
            </DialogTitle>

            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                <DialogContent sx={{ p: 0 }}>
                    {itemsCount === 0 ? (
                        <Box
                            p={4}
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            minHeight="300px"
                        >
                            <Typography variant="body1" color="text.secondary" gutterBottom>
                                No simulations added yet. Import data from clipboard to get started.
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<ContentPasteIcon />}
                                onClick={handleImportFromClipboard}
                                disabled={isImporting}
                                sx={{ mt: 2 }}
                            >
                                {isImporting ? "Importing..." : "Import CSV from Clipboard"}
                            </Button>
                        </Box>
                    ) : (
                        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
                            <Box p={2} display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="subtitle1">
                                    {itemsCount} {itemsCount === 1 ? "simulation" : "simulations"} ready to create
                                </Typography>
                                <Stack direction="row" spacing={2}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<ContentPasteIcon />}
                                        onClick={handleImportFromClipboard}
                                        disabled={isImporting}
                                    >
                                        Import More
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="warning"
                                        size="small"
                                        startIcon={<RestartAltIcon />}
                                        onClick={() => {
                                            reset(defaultValues);
                                        }}
                                    >
                                        Reset
                                    </Button>
                                </Stack>
                            </Box>
                            <Divider />
                            <Box sx={{ flexGrow: 1, height: 400, width: "100%" }}>
                                <DataGrid
                                    columns={columns}
                                    rows={watch("items")}
                                    disableRowSelectionOnClick
                                    rowHeight={100}
                                    sx={{
                                        border: "none",
                                        "& .MuiDataGrid-cell": {
                                            borderBottom: "1px solid #f0f0f0",
                                        },
                                    }}
                                />
                            </Box>
                        </Box>
                    )}

                    <Divider />
                    <Box p={3}>
                        <ExportSimulation />
                    </Box>
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2, bgcolor: "background.default" }}>
                    <Button color="inherit" variant="outlined" onClick={onClose} startIcon={<CloseIcon />}>
                        Cancel
                    </Button>
                    <LoadingButton
                        type="submit"
                        variant="contained"
                        loading={isSubmitting}
                        disabled={itemsCount === 0}
                        startIcon={<SaveIcon />}
                    >
                        Create Simulations
                    </LoadingButton>
                </DialogActions>
            </FormProvider>
        </Dialog>
    );
};
